import { atom, useAtomValue } from 'jotai'
import type { SelectedCardInfo } from '~/features/reservationInput/store/payment'
import { selectedCardInfo$ } from '~/features/reservationInput/store/payment'
import type { ReservePaymentType } from '~/lib/graphql/graphql'
import { disabledOnlineCard, disabledOnsite } from './lib/checkDisabled'
import { card$, paymentMethod$, userType$ } from './stateMachine/context'
import type {
  ReserveInputCard,
  ReserveInputCards,
  ReserveInputPlan,
  ReserveInputReserveInfo,
} from './stateMachine/state'
import {
  cards$,
  isGuest$,
  isMemberOrPreMember$,
  plan$,
  reserveInfo$,
  restaurant$,
} from './stateMachine/state'
import type { SelectedCard, UserType } from './stateMachine/statemachine'

export const disabledOnlineCard$ = atom((get) => {
  return disabledOnlineCard(get(plan$), get(restaurant$))
})
disabledOnlineCard$.debugLabel = 'disabledOnlineCard$'

export const disabledOnsite$ = atom((get) => {
  return disabledOnsite(get(plan$))
})
disabledOnsite$.debugLabel = 'disabledOnsite$'

export function useDisabledOnsite() {
  return useAtomValue(disabledOnsite$)
}

const isPaymentMethodVisible$ = atom((get) => {
  return isPaymentMethodVisible(
    get(isMemberOrPreMember$),
    get(disabledOnlineCard$),
    get(disabledOnsite$)
  )
})
isPaymentMethodVisible$.debugLabel = 'isPaymentMethodVisible$'

export function useIsPaymentMethodVisible() {
  return useAtomValue(isPaymentMethodVisible$)
}

function isPaymentMethodVisible(
  isMemberOrPreMember: boolean,
  disabledOnline: boolean,
  disabledOnsite: boolean
) {
  if (!disabledOnsite && !disabledOnline) {
    return isMemberOrPreMember
  }
  // オンラインカード決済不可の場合、ゲスト/会員に関わらず非表示になる
  return !disabledOnline
}
if (import.meta.vitest) {
  const { describe, it, expect } = import.meta.vitest
  describe('isPaymentMethodVisible', async () => {
    it('オンラインカード決済不可の場合、ゲスト/会員に関わらず非表示になる', () => {
      expect(isPaymentMethodVisible(false, true, true)).toBe(false)
      expect(isPaymentMethodVisible(true, true, true)).toBe(false)
      expect(isPaymentMethodVisible(false, true, false)).toBe(false)
      expect(isPaymentMethodVisible(true, true, false)).toBe(false)
    })
    it('現地/オンラインカード決済可能かつゲストの場合、非表示になる', () => {
      expect(isPaymentMethodVisible(false, false, false)).toBe(false)
    })
    it('上記2つ以外のケースは表示する', () => {
      expect(isPaymentMethodVisible(false, false, true)).toBe(true)
      expect(isPaymentMethodVisible(true, false, false)).toBe(true)
      expect(isPaymentMethodVisible(true, false, true)).toBe(true)
    })
  })
}

type PaymentMethodMessageType =
  | 'NO_MESSAGE'
  | 'GMO_MAINTENANCE'
  | 'REQUIRED_PASSWORD'
  | 'CARD_ONLY'
  | 'NO_REGISTERED_CARD'

const paymentMethodMessageType$ = atom<PaymentMethodMessageType>((get) => {
  return toPaymentMethodMessageType({
    card: get(selectedCardInfo$),
    paymentMethod: get(paymentMethod$),
    reserveInfo: get(reserveInfo$),
    userType: get(userType$),
    disabledOnsite: get(disabledOnsite$),
  })
})

export function usePaymentMethodMessageType() {
  return useAtomValue(paymentMethodMessageType$)
}

function toPaymentMethodMessageType(data: {
  paymentMethod: ReservePaymentType
  disabledOnsite: boolean
  reserveInfo?: ReserveInputReserveInfo
  card?: SelectedCardInfo
  userType?: UserType
}): PaymentMethodMessageType {
  // 仕様：./specification.md
  if (data.reserveInfo?.gmoMaintenance) {
    return 'GMO_MAINTENANCE'
  }
  if (data.userType === 'GUEST') {
    return 'REQUIRED_PASSWORD'
  }
  if (data.card === undefined) {
    if (data.disabledOnsite) {
      return 'CARD_ONLY'
    }
    if (data.paymentMethod === 'ONLINE') {
      return 'NO_REGISTERED_CARD'
    }
  }
  return 'NO_MESSAGE'
}
if (import.meta.vitest) {
  const { it, expect } = import.meta.vitest
  it('toPaymentMethodMessageType', async () => {
    const card = { cardId: '1' } as SelectedCardInfo
    expect(
      toPaymentMethodMessageType({
        disabledOnsite: false,
        paymentMethod: 'ONSITE',
        reserveInfo: { gmoMaintenance: true } as ReserveInputReserveInfo,
      })
    ).toBe('GMO_MAINTENANCE')
    expect(
      toPaymentMethodMessageType({
        disabledOnsite: false,
        paymentMethod: 'ONLINE',
      })
    ).toBe('NO_REGISTERED_CARD')
    expect(
      toPaymentMethodMessageType({
        disabledOnsite: true,
        paymentMethod: 'ONSITE',
        userType: 'GUEST',
      })
    ).toBe('REQUIRED_PASSWORD')
    expect(
      toPaymentMethodMessageType({
        disabledOnsite: false,
        paymentMethod: 'ONSITE',
        userType: 'GUEST',
      })
    ).toBe('REQUIRED_PASSWORD')
    expect(
      toPaymentMethodMessageType({
        disabledOnsite: false,
        paymentMethod: 'ONLINE',
        userType: 'PRE_MEMBER',
      })
    ).toBe('NO_REGISTERED_CARD')
    expect(
      toPaymentMethodMessageType({
        disabledOnsite: true,
        paymentMethod: 'ONLINE',
        userType: 'PRE_MEMBER',
      })
    ).toBe('CARD_ONLY')
    expect(
      toPaymentMethodMessageType({
        disabledOnsite: false,
        paymentMethod: 'ONLINE',
        userType: 'PRE_MEMBER',
        card,
      })
    ).toBe('NO_MESSAGE')
    expect(
      toPaymentMethodMessageType({
        disabledOnsite: true,
        paymentMethod: 'ONLINE',
        userType: 'PRE_MEMBER',
        card,
      })
    ).toBe('NO_MESSAGE')
    expect(
      toPaymentMethodMessageType({
        disabledOnsite: false,
        paymentMethod: 'ONSITE',
        userType: 'PRE_MEMBER',
      })
    ).toBe('NO_MESSAGE')
    expect(
      toPaymentMethodMessageType({
        disabledOnsite: false,
        paymentMethod: 'ONSITE',
        userType: 'PRE_MEMBER',
        card,
      })
    ).toBe('NO_MESSAGE')
    expect(
      toPaymentMethodMessageType({
        disabledOnsite: false,
        paymentMethod: 'ONLINE',
        userType: 'MEMBER',
      })
    ).toBe('NO_REGISTERED_CARD')
    expect(
      toPaymentMethodMessageType({
        disabledOnsite: true,
        paymentMethod: 'ONLINE',
        userType: 'MEMBER',
      })
    ).toBe('CARD_ONLY')
    expect(
      toPaymentMethodMessageType({
        disabledOnsite: false,
        paymentMethod: 'ONLINE',
        userType: 'MEMBER',
        card,
      })
    ).toBe('NO_MESSAGE')
    expect(
      toPaymentMethodMessageType({
        disabledOnsite: true,
        paymentMethod: 'ONLINE',
        userType: 'MEMBER',
        card,
      })
    ).toBe('NO_MESSAGE')
    expect(
      toPaymentMethodMessageType({
        disabledOnsite: false,
        paymentMethod: 'ONSITE',
        userType: 'MEMBER',
      })
    ).toBe('NO_MESSAGE')
    expect(
      toPaymentMethodMessageType({
        disabledOnsite: false,
        paymentMethod: 'ONSITE',
        userType: 'MEMBER',
        card,
      })
    ).toBe('NO_MESSAGE')
  })
}

export const noCardRegisteredErrorMessage$ = atom((get) => {
  return noCardRegisteredErrorMessage(get(paymentMethod$), get(cards$), get(card$))
})
noCardRegisteredErrorMessage$.debugLabel = 'noCardRegisteredErrorMessage$'

function noCardRegisteredErrorMessage(
  paymentMethod: ReservePaymentType,
  myCards: ReserveInputCards,
  selected?: SelectedCard
) {
  if (paymentMethod === 'ONLINE' && myCards.length === 0 && selected === undefined) {
    return 'カード情報を登録してください。'
  }
  return ''
}
if (import.meta.vitest) {
  const { it, expect } = import.meta.vitest
  it('noCardRegisteredErrorMessage', async () => {
    expect(noCardRegisteredErrorMessage('ONLINE', [])).toBe('カード情報を登録してください。')
    expect(noCardRegisteredErrorMessage('ONLINE', [], { type: 'new' } as SelectedCard)).toBe('')
    expect(
      noCardRegisteredErrorMessage('ONLINE', [{ cardId: '1' } as unknown as ReserveInputCard])
    ).toBe('')
    expect(noCardRegisteredErrorMessage('ONSITE', [])).toBe('')
  })
}

export const memberRequiredCardPaymentErrorMessage$ = atom((get) => {
  return memberRequiredCardPaymentErrorMessage(get(isGuest$), get(plan$))
})
memberRequiredCardPaymentErrorMessage$.debugLabel = 'memberRequiredCardPaymentErrorMessage$'

export function useMemberRequiredCardPaymentErrorMessage() {
  return useAtomValue(memberRequiredCardPaymentErrorMessage$)
}

function memberRequiredCardPaymentErrorMessage(isGuest: boolean, plan?: ReserveInputPlan) {
  // ゲストでオンラインカード決済限定プランを予約する場合は会員登録しなければ予約できない
  if (isGuest && plan?.paymentType === 'ONLINE_ONLY') {
    return 'オンラインカード決済を利用される場合は、会員登録または会員ログインが必須となります。'
  }
  return ''
}
if (import.meta.vitest) {
  const { it, expect } = import.meta.vitest
  it('memberRequiredCardPaymentErrorMessage', async () => {
    expect(
      memberRequiredCardPaymentErrorMessage(true, {
        paymentType: 'ONLINE_ONLY',
      } as ReserveInputPlan)
    ).toBe('オンラインカード決済を利用される場合は、会員登録または会員ログインが必須となります。')
    expect(
      memberRequiredCardPaymentErrorMessage(false, {
        paymentType: 'ONLINE_ONLY',
      } as ReserveInputPlan)
    ).toBe('')
    expect(
      memberRequiredCardPaymentErrorMessage(true, {
        paymentType: 'ONSITE_ONLY',
      } as ReserveInputPlan)
    ).toBe('')
  })
}
